import type { HelpLang } from './knowledge';
import { resolveAssistantAction, TOPIC_ACTION_IDS } from './assistantActions';

export type AssistantIntent =
  | 'greeting'
  | 'capabilities'
  | 'navigation'
  | 'game_rules'
  | 'role_help'
  | 'tech_support'
  | 'report_issue'
  | 'request_human'
  | 'topic'
  | 'clarify'
  | 'unknown';

export interface DetectedIntent {
  intent: AssistantIntent;
  confidence: number;
}

const CAPABILITY_RE = /\b(qu[eé]\s+puedes\s+(hacer|ayudar)|what\s+can\s+you\s+do|para\s+qu[eé]\s+sirves|help\s+me\s+with|c[oó]mo\s+me\s+ayudas|your\s+capabilities)\b/i;
const HUMAN_RE = /\b(agente|humano|persona|live\s*chat|hablar\s+con|talk\s+to\s+(a\s+)?(human|agent)|soporte\s+en\s+vivo)\b/i;
const TICKET_RE = /\b(ticket|reporte|reportar|abrir\s+ticket|create\s+a?\s*ticket|file\s+a?\s*(ticket|report))\b/i;
const NAV_RE = /\b(d[oó]nde|where|c[oó]mo\s+(llego|abro|entro)|open|abrir|ir\s+a|go\s+to|navegar)\b/i;
const TECH_RE = /\b(no\s+carga|error|bug|crash|offline|sin\s+conexi[oó]n|login|contrase[nñ]a|password|mfa|biometric|no\s+funciona|broken)\b/i;
const GAME_RE = /\b(fijo|corrido|parle|centena|charada|premio|payout|regla|rules|c[oó]mo\s+se\s+juega|bolita)\b/i;

export function detectAssistantIntent(message: string): DetectedIntent {
  const text = message.trim();
  if (!text) return { intent: 'greeting', confidence: 0.9 };
  if (/^(hi|hello|hola|hey|buenas|buenos)\b/i.test(text) && text.length < 24) {
    return { intent: 'greeting', confidence: 0.95 };
  }
  if (CAPABILITY_RE.test(text) || /^qu[eé]\s+puedes/i.test(text)) {
    return { intent: 'capabilities', confidence: 0.95 };
  }
  if (HUMAN_RE.test(text)) return { intent: 'request_human', confidence: 0.9 };
  if (TICKET_RE.test(text)) return { intent: 'report_issue', confidence: 0.85 };
  if (TECH_RE.test(text)) return { intent: 'tech_support', confidence: 0.75 };
  if (NAV_RE.test(text)) return { intent: 'navigation', confidence: 0.7 };
  if (GAME_RE.test(text)) return { intent: 'game_rules', confidence: 0.7 };
  return { intent: 'unknown', confidence: 0.4 };
}

export function buildCapabilitiesReply(lang: HelpLang, role?: string): string {
  if (lang === 'es') {
    return [
      'Puedo ayudarte a:',
      '• Explicar topes, cierres, jugadas, resultados y rutas según tu rol',
      '• Guiarte con enlaces a pantallas de la app',
      '• Diagnosticar problemas básicos (acceso, red, MFA)',
      '• Si hace falta, ofrecerte chat con un agente o un ticket — solo si lo confirmas',
      role ? `Ahora estás como ${role}.` : '',
      '¿Qué quieres hacer?',
    ].filter(Boolean).join('\n');
  }
  return [
    'I can help you:',
    '• Explain caps, cutoffs, plays, results, and routes for your role',
    '• Guide you with links to app screens',
    '• Troubleshoot basic issues (access, network, MFA)',
    '• Offer live agent chat or a ticket — only if you confirm',
    role ? `You’re signed in as ${role}.` : '',
    'What would you like to do?',
  ].filter(Boolean).join('\n');
}

export function buildHandoffOfferReply(lang: HelpLang, concern: string): string {
  const short = concern.trim().slice(0, 100);
  if (lang === 'es') {
    return `No tengo una guía exacta para “${short}”. ¿Quieres hablar con un agente en vivo, abrir un ticket de soporte, o seguir conmigo aclarando el detalle?`;
  }
  return `I don’t have an exact guide for “${short}”. Would you like to talk to a live agent, open a support ticket, or keep clarifying with me?`;
}

export function actionRefsForTopic(topicId: string, role?: string, lang: HelpLang = 'es') {
  const actionId = TOPIC_ACTION_IDS[topicId];
  if (!actionId) return [];
  const resolved = resolveAssistantAction(actionId, role, lang);
  return resolved ? [{ id: resolved.id, label: resolved.label, target: resolved.target }] : [];
}
